// packages import
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, IconButton, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { FC, useContext, useEffect, useState } from "react";
// component import
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { Alert } from '../../common/Alert';
import { CustomPagination } from '../../common/CustomPagination';
import { EmptyData } from "../../common/EmptyData";
import { StickyTable } from "../../common/StickyTable";
import { JudgeAgainstSkeletonListing } from '../../skeletons/JudgeAgainstCourtListing';
import { AddJudge } from "./AddJudgeModal";
import { RemoveJudgeFromCourt } from "./RemoveJudgeFromCourt";
// others
import { ExternalUser, ExternalUserRoleRole, Maybe, useFetchAllExternalUsersLazyQuery } from "../../../../generated";
import { GRAPHQL_QUERY_POLICY, JUDGE_AGAINST_COURT_TABLE_HEADERS, NOT_FOUND_EXCEPTION, SOMETHING_WENT_WRONG } from "../../../constants";
import { AppContext, AuthContext } from "../../../context";
import { ActionType } from "../../../context/AppContextReducer";
import { JudgeListingProps } from "../../../interfaceTypes";
import { flexCenter } from "../../../theme/styleConstants";

export const JudgeListing: FC<JudgeListingProps> = ({ courtId, courtName }) => {
  const { isOpen, dispatch, isDeleteOpen, pageLimit, currentPage } = useContext(AppContext)
  const { isParalegal, isAttorney } = useContext(AuthContext)
  const [currentExternalUser, setCurrentExternalUser] = useState<ExternalUser>()
  const [externalUsersData, setExternalUsersData] = useState<(Maybe<ExternalUser> | undefined)[]>([])
  const [currentExistingJudgePage, setCurrentExistingJudgePage] = useState<number>(1);
  const [currentExistingJudgeTotalPage, setCurrentExistingJudgeTotalPage] = useState<number>(1);
  const [judgesData, setJudgesData] = useState<(Maybe<ExternalUser> | undefined)[]>()

  const [fetchAllExternalUsers, { refetch: refetchAllExternalUsers, loading: externalUserLoading }] = useFetchAllExternalUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError({ message }) {
      if (message !== NOT_FOUND_EXCEPTION) {
        Alert.error(SOMETHING_WENT_WRONG)
      }
      setExternalUsersData([])
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllExternalUsers } = data

        if (fetchAllExternalUsers) {
          const { externalUsers, response, pagination } = fetchAllExternalUsers
          const { status } = response || {}

          if (status === 200 && externalUsers) {
            setExternalUsersData(externalUsers)
          }

          if (pagination) {
            const { totalPages, totalCount } = pagination

            totalPages && dispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages });
            totalCount && dispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
          }
        }
      }
    },
  })

  const [fetchAllAlreadyExistingJudges, { refetch: refetchExistingJudges }] = useFetchAllExternalUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      setJudgesData([])
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllExternalUsers } = data

        if (fetchAllExternalUsers) {
          const { externalUsers, response, pagination } = fetchAllExternalUsers
          const { status } = response || {}

          if (status === 200 && externalUsers) {
            if (currentExistingJudgeTotalPage >= currentExistingJudgePage) {
              judgesData ? setJudgesData([...judgesData, ...externalUsers]) : setJudgesData(externalUsers)
            }
            setJudgesData(externalUsers)
          }

          if (pagination) {
            const { totalPages } = pagination

            if (totalPages && totalPages > 0) {
              setCurrentExistingJudgeTotalPage(totalPages)
              setCurrentExistingJudgePage(currentPage + 1)
            }
          }
        }
      }
    },
  })

  const handleAddJudgeModal = () => {
    dispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })
  }

  const handleRemoveJudgeFromCourt = (externalUser: ExternalUser) => {
    dispatch({ type: ActionType.SET_IS_DELETE_OPEN, isDeleteOpen: !isDeleteOpen })
    setCurrentExternalUser(externalUser)
  }

  useEffect(() => {
    fetchAllExternalUsers({
      variables: {
        externalUsersInput: {
          courtId,
          roles: [ExternalUserRoleRole.Judge],

          paginationOptions: {
            limit: pageLimit,
            page: currentPage
          }
        }
      }
    })
    // eslint-disable-next-line
  }, [currentPage])

  useEffect(() => {
    if (currentExistingJudgeTotalPage >= currentExistingJudgePage) {
      fetchAllAlreadyExistingJudges({
        variables: {
          externalUsersInput: {
            courtId: courtId,
            roles: [ExternalUserRoleRole.Judge],

            paginationOptions: {
              limit: 1000,
              page: currentExistingJudgePage
            }
          }
        }
      })
    }
    // eslint-disable-next-line
  }, [currentExistingJudgeTotalPage, currentExistingJudgePage])
  const isEmpty = externalUsersData?.length === 0 && !externalUserLoading

  return (
    <>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography fontSize='20px'>
          Assigned Judges
        </Typography>

        {(isParalegal || isAttorney) &&
          <Button onClick={handleAddJudgeModal} variant="contained" endIcon={<PersonAddAlt1Icon />} aria-label='add judge'>
            add
          </Button>
        }
      </Box>

      {isEmpty ?
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 328px)' }}>
          <EmptyData title='No judge assigned to this court' />
        </Box>
        :
        <StickyTable height='390px' pagination={<CustomPagination loading={externalUserLoading || false} />}>
          <TableHead>
            <TableRow>
              <TableCell>
                Judge
              </TableCell>
              {JUDGE_AGAINST_COURT_TABLE_HEADERS.map((item, index) => {
                const lastIndex = JUDGE_AGAINST_COURT_TABLE_HEADERS.length - 1 === index

                return (
                  <TableCell component="th" key={`${item}-${index}`} sx={{ minWidth: lastIndex ? 'unset' : '180px' }} align={lastIndex ? 'center' : 'left'}>
                    {item}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>

          {externalUserLoading
            ?
            <JudgeAgainstSkeletonListing noOfRows={4} />
            :
            <TableBody>
              {externalUsersData?.map((judgeUser) => {
                const { id, firstName, lastName, email, phoneNumber, phoneNumberExtension } = judgeUser || {}
                return (
                  <TableRow key={id} hover>
                    <TableCell>
                      <Box maxWidth='250px'>
                        <Tooltip title={`${firstName || '--'} ${lastName}`} arrow placement='top-start'>
                          <Typography variant="body1" noWrap>
                            {`${firstName || '--'} ${lastName}`}
                          </Typography>
                        </Tooltip>

                        <Tooltip title={email} arrow>
                          <Typography variant="body2" noWrap color='wardGray' fontWeight='normal'>
                            {email || 'N/A'}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </TableCell>

                    <TableCell>
                      {phoneNumberExtension || 'N/A'}
                    </TableCell>

                    <TableCell>
                      {phoneNumber || 'N/A'}
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title='Remove judge from court' arrow placement='top-start'>
                        <IconButton onClick={() => handleRemoveJudgeFromCourt(judgeUser!)} aria-label='delete judge'>
                          <DeleteIcon color="primary" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          }
        </StickyTable>
      }

      <AddJudge
        isOpen={isOpen}
        courtId={courtId}
        judgesData={judgesData || []}
        reFetchCourt={refetchAllExternalUsers}
        refetchExistingJudges={refetchExistingJudges}
      />

      <RemoveJudgeFromCourt
        isOpen={isDeleteOpen}
        currentExternalUser={currentExternalUser!}
        courtId={courtId}
        reFetchCourt={refetchAllExternalUsers}
        courtName={courtName}
        refetchExistingJudges={refetchExistingJudges}
      />
    </>
  )
}